/**
 * The Filter_Context codec: the one place the Initiative filter grammar is read
 * from and written to a URL search string (`complan-governance` Requirement 6
 * criteria 1, 2, 5; Foundation R11.13).
 *
 * Every screen that filters Initiatives - the Calendar_View, the Initiative list,
 * the Governance_Dashboard, the Management_Dashboard and the roll-up views - shares
 * this codec. What each screen keeps for itself is the state that is *its own*: the
 * calendar's orientation and path year, the list's page window. Those live in the
 * per-screen modules that wrap this one, because a shared codec that also owned
 * them would give every screen a default for a control it does not have.
 *
 * ## Why the dimension lists are derived rather than written out
 *
 * They used to be two hand-maintained arrays in `calendar-search-params.ts`, and
 * they had already drifted: `complan-governance` added `hasUnresolvedWatchout`,
 * `watchoutSeverities` and `milestoneReadiness` to `initiativeFilterSchema`, the
 * arrays were not updated, and the consequence was that those three dimensions
 * could not appear in any URL at all. A Filter_Context that cannot carry a
 * dimension cannot survive a navigation (R6.3) or a reload (R6.5), so the drift was
 * not cosmetic.
 *
 * {@link FILTER_DIMENSION_KIND} fixes that by construction. It is a **total**
 * mapping over `keyof InitiativeFilter`, so a dimension added to the contract
 * without a classification here is a compile error rather than a dimension that
 * silently stops working - the same "exhaustive by construction" device the domain
 * uses for the Bulk_Edit allowlist. The key arrays and their types are both derived
 * from it, so there is one list, not three that must agree.
 *
 * ## Representation
 *
 * Multi-value dimensions are repeated params (`?platformIds=a&platformIds=b`)
 * rather than comma-joined, matching the contracts filter grammar, which accepts
 * either. Tri-state Booleans serialise as `true` / `false`; an absent param means
 * the dimension is unfiltered, which is why serialisation omits every dimension
 * left at its default and a default view has an empty query string.
 *
 * Nothing here throws. An unparseable value is dropped and the schema default
 * applies, so a hand-edited, bookmarked or stale URL still opens (R11.15, R11.16).
 */
import type { InitiativeFilter } from '@complan/contracts';

/**
 * How one filter dimension is carried in a URL.
 *
 * - `list` - repeated params, unfiltered when absent.
 * - `boolean` - `true` / `false`, unfiltered when absent.
 * - `search` - the free-text term; blank is unfiltered, never `''`.
 * - `year` - the planning year, which **no screen carries as a query param**: the
 *   calendar holds it in its route path and the Initiative list supplies the
 *   current system year. It is classified rather than omitted so the mapping stays
 *   total, and {@link parseFilterDimensions} skips it for that reason.
 */
export type FilterDimensionKind = 'list' | 'boolean' | 'search' | 'year';

/**
 * Every dimension of `InitiativeFilter`, classified.
 *
 * `satisfies` is doing the load-bearing work: it holds the object to
 * `Record<keyof InitiativeFilter, FilterDimensionKind>` - so a missing dimension
 * fails the build - while `as const` keeps the literal types the derived key types
 * below need. Neither alone is enough.
 */
export const FILTER_DIMENSION_KIND = {
  planningYear: 'year',

  // Timing (R11.2).
  salesCycleIds: 'list',
  months: 'list',
  isoWeeks: 'list',

  // Classification (R11.2).
  platformIds: 'list',
  channelIds: 'list',
  customerGroupIds: 'list',
  audienceIds: 'list',
  categoryIds: 'list',
  initiativeTypeIds: 'list',
  ownerUserIds: 'list',
  statuses: 'list',
  priorityIds: 'list',

  // Governance and involvement flags (R11.4, R11.6).
  confidential: 'boolean',
  fieldForceInvolvement: 'boolean',
  fieldForceRoles: 'list',
  fieldForceMaterial: 'boolean',
  hasWorkOrder: 'boolean',
  hasInformationContribution: 'boolean',
  dteInvolvement: 'boolean',
  cscInvolvement: 'boolean',
  openDependencies: 'boolean',

  // `complan-governance` R6.11, R6.12. Absent from the hand-written arrays this
  // module replaces, which is the drift that motivated deriving them.
  hasUnresolvedWatchout: 'boolean',
  watchoutSeverities: 'list',
  milestoneReadiness: 'list',

  search: 'search',
} as const satisfies Record<keyof InitiativeFilter, FilterDimensionKind>;

/** The dimension keys of one kind, as a union of literal types. */
type DimensionKeyOfKind<TKind extends FilterDimensionKind> = {
  readonly [TKey in keyof typeof FILTER_DIMENSION_KIND]: (typeof FILTER_DIMENSION_KIND)[TKey] extends TKind
    ? TKey
    : never;
}[keyof typeof FILTER_DIMENSION_KIND];

/** A list-valued filter dimension key. */
export type ListFilterKey = DimensionKeyOfKind<'list'>;

/** A tri-state Boolean filter dimension key. */
export type BooleanFilterKey = DimensionKeyOfKind<'boolean'>;

/** Any dimension a chip or a clear control can address. */
export type ClearableFilterKey = ListFilterKey | BooleanFilterKey | 'search';

const DIMENSION_ENTRIES = Object.entries(FILTER_DIMENSION_KIND) as readonly (readonly [
  keyof typeof FILTER_DIMENSION_KIND,
  FilterDimensionKind,
])[];

/**
 * Every list-valued dimension, in declaration order.
 *
 * Derived, so it cannot disagree with {@link FILTER_DIMENSION_KIND}. Declaration
 * order is what makes serialisation deterministic: two identical filters must
 * produce the same query string, which is half of Property 56's round trip.
 */
export const LIST_KEYS: readonly ListFilterKey[] = Object.freeze(
  DIMENSION_ENTRIES.filter(([, kind]) => kind === 'list').map(([key]) => key as ListFilterKey),
);

/** Every tri-state Boolean dimension, in declaration order. */
export const BOOLEAN_KEYS: readonly BooleanFilterKey[] = Object.freeze(
  DIMENSION_ENTRIES.filter(([, kind]) => kind === 'boolean').map(
    ([key]) => key as BooleanFilterKey,
  ),
);

/* ────────────────────────────────────────────────────────────────────────
   Reading
   ──────────────────────────────────────────────────────────────────────── */

/**
 * A repeated or comma-joined param as a list of non-empty trimmed values.
 *
 * Accepts both spellings because the contracts filter grammar does, so a URL a
 * human shortened by hand to `?statuses=draft,planned` behaves as the repeated form.
 */
export function readList(search: URLSearchParams, key: string): string[] {
  return search
    .getAll(key)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

/** A tri-state Boolean: `true`, `false`, or `undefined` for unfiltered. */
export function readBoolean(search: URLSearchParams, key: string): boolean | undefined {
  const value = search.get(key);
  if (value === 'true') return true;
  if (value === 'false') return false;
  return undefined;
}

/**
 * The free-text term, trimmed, with a blank box reported as `null` rather than `''`.
 *
 * The distinction matters: `''` would be a dimension matching every row, where the
 * intent of an empty box is no dimension at all.
 */
export function readSearchTerm(search: URLSearchParams): string | null {
  const raw = search.get('search');
  if (raw === null) {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Every filter dimension present in `search`, as a raw record ready for a schema
 * parse.
 *
 * A dimension left out of the result rather than set to an empty value, so the
 * schema's own default decides what "unfiltered" is - this module never encodes a
 * second opinion about that. `planningYear` is skipped: no screen carries it as a
 * query param (see {@link FilterDimensionKind}), and the caller supplies it.
 */
export function parseFilterDimensions(search: URLSearchParams): Record<string, unknown> {
  const raw: Record<string, unknown> = {};

  for (const key of LIST_KEYS) {
    const values = readList(search, key);
    if (values.length > 0) {
      raw[key] = values;
    }
  }
  for (const key of BOOLEAN_KEYS) {
    const value = readBoolean(search, key);
    if (value !== undefined) {
      raw[key] = value;
    }
  }
  const term = readSearchTerm(search);
  if (term !== null) {
    raw['search'] = term;
  }

  return raw;
}

/* ────────────────────────────────────────────────────────────────────────
   Writing
   ──────────────────────────────────────────────────────────────────────── */

/**
 * The dimensions of `filter` that are not at their unfiltered value, appended to
 * `params` in declaration order.
 *
 * Mutates the `params` it is given, because every caller has just created it; the
 * mutation helpers below are the ones that must not mutate, and they do not.
 */
export function writeFilterDimensions(
  params: URLSearchParams,
  filter: Pick<InitiativeFilter, ListFilterKey | BooleanFilterKey | 'search'>,
): void {
  for (const key of LIST_KEYS) {
    for (const value of filter[key]) {
      params.append(key, String(value));
    }
  }
  for (const key of BOOLEAN_KEYS) {
    const value = filter[key];
    if (value !== null) {
      params.set(key, String(value));
    }
  }
  if (filter.search !== null) {
    params.set('search', filter.search);
  }
}

/** Whether any filter dimension of `filter` is applied. */
export function hasAnyFilter(
  filter: Pick<InitiativeFilter, ListFilterKey | BooleanFilterKey | 'search'>,
): boolean {
  if (filter.search !== null) return true;
  if (LIST_KEYS.some((key) => filter[key].length > 0)) return true;
  return BOOLEAN_KEYS.some((key) => filter[key] !== null);
}

/* ────────────────────────────────────────────────────────────────────────
   Mutation helpers.

   Every filter control mutates the URL through one of these, so a control never
   assembles a query string of its own and the single source of truth (R11.13) stays
   here. Each takes the *current* `URLSearchParams` and returns a fresh one, matching
   react-router's `setSearchParams(updater)` shape: they never mutate their argument.
   ──────────────────────────────────────────────────────────────────────── */

/** Replace the whole value set of one multi-value dimension (empty clears it). */
export function setListDimension(
  current: URLSearchParams,
  key: ListFilterKey,
  values: readonly string[],
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete(key);
  for (const value of values) {
    if (value.length > 0) {
      next.append(key, value);
    }
  }
  return next;
}

/** Set a tri-state Boolean dimension; `undefined` returns it to "unfiltered". */
export function setBooleanDimension(
  current: URLSearchParams,
  key: BooleanFilterKey,
  value: boolean | undefined,
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (value === undefined) {
    next.delete(key);
  } else {
    next.set(key, String(value));
  }
  return next;
}

/**
 * Set the free-text Search term; an empty or whitespace-only term returns the
 * dimension to "unfiltered" rather than applying a term that matches every row.
 */
export function setSearchTerm(current: URLSearchParams, term: string): URLSearchParams {
  const next = new URLSearchParams(current);
  const trimmed = term.trim();
  if (trimmed === '') {
    next.delete('search');
  } else {
    next.set('search', trimmed);
  }
  return next;
}

/**
 * Clear a single dimension back to its unfiltered value, list or Boolean alike.
 * This is what a filter chip's remove control calls (UI spec §3.7): removing a chip
 * is clearing the dimension it stands for.
 */
export function clearDimension(
  current: URLSearchParams,
  key: ClearableFilterKey,
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete(key);
  return next;
}

/**
 * Remove a single value from a multi-value dimension, leaving its other values in
 * place. A dimension chip that renders one value per chip removes just that value.
 */
export function removeListValue(
  current: URLSearchParams,
  key: ListFilterKey,
  value: string,
): URLSearchParams {
  const remaining = readList(current, key).filter((entry) => entry !== value);
  return setListDimension(current, key, remaining);
}

/**
 * Clear every filter dimension, preserving the params named in `keep`.
 *
 * `keep` is how a screen protects its own view state - the calendar's orientation
 * and sort are layout, not filters, and survive a filter reset (R11.1). A screen
 * passes the keys it owns; this module does not need to know what they mean.
 */
export function clearAllDimensions(
  current: URLSearchParams,
  keep: readonly string[],
): URLSearchParams {
  const next = new URLSearchParams();
  for (const key of keep) {
    const value = current.get(key);
    if (value !== null) {
      next.set(key, value);
    }
  }
  return next;
}
